/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react';

/**
 * Drives a horizontal (column) drag-to-resize interaction. Returns a
 * pointer-down handler to attach to a divider or panel edge; while the pointer
 * is held it forwards every `pointermove` to `onMove`, which is responsible for
 * applying the new width. Listeners are registered on `window` so the drag keeps
 * tracking outside the handle, and are torn down on pointerup/cancel/blur and on
 * unmount.
 *
 * `onEnd` fires once the pointer is released (not on unmount), which is where a
 * caller commits the dragged width to React state / storage. Keeping that out of
 * `onMove` matters: committing per move event means a synchronous localStorage
 * write and a re-render — and a width inside `sx` re-serializes emotion styles on
 * every pixel.
 */
export function useHorizontalResize(
  onMove: (event: globalThis.PointerEvent) => void,
  onEnd?: () => void,
): (event: ReactPointerEvent<HTMLElement>) => void {
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => () => cleanupRef.current?.(), []);

  return useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      cleanupRef.current?.();
      const abortController = new AbortController();
      const cleanup = () => {
        abortController.abort();
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (cleanupRef.current === cleanup) {
          cleanupRef.current = undefined;
        }
      };
      cleanupRef.current = cleanup;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      // Only a real pointer release commits; unmount cleanup must not set state.
      const endDrag = () => {
        cleanup();
        onEndRef.current?.();
      };
      const options = { signal: abortController.signal };
      window.addEventListener('pointermove', onMove, options);
      window.addEventListener('pointerup', endDrag, options);
      window.addEventListener('pointercancel', endDrag, options);
      window.addEventListener('blur', endDrag, options);
    },
    [onMove],
  );
}
